<script>
	import { preventDefault } from 'svelte/legacy';

	import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
	import { styleState } from "@app/store/styleStore.svelte.js";
	import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
	import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
	import { projectState } from "@app/store/projectStore.svelte.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { fade } from "svelte/transition";

	/* CC LIB */
	import defaults from "@cclib/lib/defaults.js";
	import encodings from "@cclib/dict/encodings.js";
	import formats from "@cclib/lib/formats.js";
	import frameRates from "@cclib/dict/frameRates.js";
	import _EventGroup from "@cclib/classes/eventGroup.js";
	import LanguageSelect from "@app/components/inputs/LanguageSelect.svelte";

	import tcLib from "@cclib/lib/timecode.js";
	import Swal from "sweetalert2";
	import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";

	import {
		getProjectClockIncode,
		isDropFrameEnabled,
		getUseClockTimeDefault,
		readSubtitleFileContents,
		buildDecodeOptions,
		decodeSubtitleSource,
		detectAutomaticOffset,
		applyAutomaticOffset,
		stripBlankEvents,
		hasOverlaps,
		applyOverlapFix,
		importSpeakersFromGroup,
		applyDropFrameConvert,
	} from "@app/lib/subtitleDecodePipeline.js";

	let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
	let subtitleImportDefaults = JSON.parse(localStorage.getItem("cc-subtitle-import-defaults")) || {};

	/* Default Settings */
	let srcFileName,
		srcExt = $state(),
		srcProfile = $state(),
		srcFrameRate = $state(projectState.frameRate),
		srcDropFrame = $state(projectState.dropFrame ? "true" : "false"),
		//A Project Incode set by the user takes priority over the saved import default
		srcIncode = $state(projectState.incode > 0 ? getProjectClockIncode() : (subtitleImportDefaults.srcIncode || getProjectClockIncode())),
		srcUseClockTime = $state(subtitleImportDefaults.srcUseClockTime || "yes"),
		dropFrameConvert = $state(false),
		showOptions = $state(false),
		files = $state(),
		fileEncoding = $state(defaults.encoding),
		fileContents = $state(""),
		allowedExtensions = "." + formats.srcExt.join(", ."),
		eventGroup = $state("new"),
		importOption = $state(subtitleImportDefaults.importOption || "merge"),
		importTrack = $state(""),
		language = $state(eventGroupDefaults.language || "en-US"),
		rtl = $state(eventGroupDefaults.rtl);

	const TRACK_LETTERS = ["A", "B", "C", "D"];

	function isSrcDropFrameEnabled() {
		return isDropFrameEnabled(srcDropFrame);
	}

	function validateSrcIncode(e) {
		srcIncode = tcLib.tcValidate(
			e.target.value,
			parseFloat(srcFrameRate),
			isSrcDropFrameEnabled(),
		);
	}

	function updateSrcUseClockTime() {
		srcUseClockTime = getUseClockTimeDefault(srcProfile);
	}

	// More than one file selected: each file is imported into its own new Event Group
	let isMultiFile = $derived(Array.isArray(files) && files.length > 1);

	function getFileExt(file) {
		return file.name.split(".").pop().toLowerCase();
	}

	function getFileBaseName(file) {
		let nameParts = file.name.split(".");
		if (nameParts.length > 1) nameParts.pop();
		return nameParts.join(".");
	}

	// Handler for file inputs
	function handleFileInput(event) {
		const picked = Array.from(event.currentTarget.files || []);
		files = picked;
		updateFile();
	}

	function updateFile() {
		if (files[0] && files[0].name) {
			srcExt = getFileExt(files[0]);
			srcFileName = getFileBaseName(files[0]);

			if (files.length > 1) {
				eventGroup = "new";
				// The profile select only applies when every file shares the same extension
				if (files.some((file) => getFileExt(file) !== srcExt)) {
					srcExt = undefined;
				}
			}

			srcProfile = srcExt ? formats.srcProfiles[srcExt][0] : undefined;
			updateSrcUseClockTime();
			updateFileContents();
		}
	}

	async function updateFileContents() {
		if (isMultiFile) {
			fileContents = files.map((file) => file.name).join("\n");
			return;
		}

		if (files && files[0]) {
			toast.push(m.subtitle_import_reading_file_toast(), {
                classes: ["toast-warning"],
            });

			try {
				fileContents = await readSubtitleFileContents(files[0], {
					srcExt: srcExt,
					srcProfile: srcProfile,
					fileEncoding: fileEncoding,
				});
			} catch (err) {
				console.log(err, err.message);
				toast.push(
					m.subtitle_import_failed_toast({ error: err.message }),
					{ classes: ["toast-danger"] },
				);
			}
		}
	}

	/* Creates a new subtitle event group and returns its index */
	function createSubtitleEventGroup(name) {
		let eventGroupOptions = {
			type: "subtitle",
			name: name,
			language: language,
			rtl: rtl,
            maxLines : eventGroupDefaults.maxLines || 2,
            maxChars : eventGroupDefaults.maxChars || 32,
            minDuration : eventGroupDefaults.minDuration || 0.5,
            maxDuration : eventGroupDefaults.maxDuration || 12,
            overlap : eventGroupDefaults.overlap,
            fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
            fontSize: eventGroupDefaults.fontSize || 16
		};

		eventGroupState.push({...(new _EventGroup(eventGroupOptions))});
		return eventGroupState.length - 1;
	}

	/* Decodes file contents and runs the offset/blank/overlap/speaker/drop frame clean up.
	   promptAnswers holds the user's answers so a multi-file import only asks each question once. */
	async function decodeAndCleanUp(contents, profile, useClockTime, promptAnswers) {
		let options = buildDecodeOptions({
			srcProfile: profile,
			srcFrameRate: srcFrameRate,
			srcDropFrame: srcDropFrame,
			srcIncode: srcIncode,
			srcUseClockTime: useClockTime,
			formatOptions: formats.profileMapping[profile].options.decode,
			window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding
            }
		});

		//console.log("Options:", JSON.stringify(options, null, 4));
		let decodedEvGroup = await decodeSubtitleSource(contents, options);
		let offsetAmount = detectAutomaticOffset(decodedEvGroup);

		if (offsetAmount != 0) {
			try {
				if (promptAnswers.offset === undefined) {
					let offsetRes = await Swal.fire({
						...SWAL_BASE,
						customClass: SWAL_CLASSES.confirmCancelLight,
						title: m.subtitle_import_auto_offset_title(),
						text: m.subtitle_import_auto_offset_text({
                            timecode: tcLib.secToTcClock(
                                offsetAmount,
                                projectState.frameRate,
                                projectState.dropFrame
                            ),
                        }),
						icon: "question",
						showCancelButton: true,
						confirmButtonText: m.yes(),
						cancelButtonText: m.no(),
						allowOutsideClick: false,
						allowEscapeKey: false,
					});
					promptAnswers.offset = offsetRes.isConfirmed;
				}

				if (promptAnswers.offset) {
					projectState.incode = offsetAmount;
					decodedEvGroup = applyAutomaticOffset(decodedEvGroup);
				}
			} catch (err) {
				console.log(err, err.message);
			}
		}

		decodedEvGroup = stripBlankEvents(decodedEvGroup);

		//Check for overlaps in the new event group. If overlaps are found, ask the user if they want them fixed using SweetAlert2
		if (hasOverlaps(decodedEvGroup)){
			if (promptAnswers.overlap === undefined) {
				let overlapRes = await Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.confirmCancelLight,
					title: m.subtitle_import_overlap_title(),
					text: m.subtitle_import_overlap_text(),
					icon: "question",
					showCancelButton: true,
					confirmButtonText: m.yes(),
					cancelButtonText: m.no(),
					allowOutsideClick: false,
					allowEscapeKey: false,
				});
				promptAnswers.overlap = overlapRes.isConfirmed;
			}

			if (promptAnswers.overlap) {
				decodedEvGroup = applyOverlapFix(decodedEvGroup);
			}
		}

        //Import Speakers from events
        importSpeakersFromGroup(decodedEvGroup);

		if (dropFrameConvert) {
			decodedEvGroup = applyDropFrameConvert(decodedEvGroup);
		}

		return decodedEvGroup;
	}

	function saveImportDefaults() {
		localStorage.setItem(
			"cc-subtitle-import-defaults",
			JSON.stringify({
				showOptions: showOptions,
				importOption: importOption,
				srcIncode: srcIncode,
				srcUseClockTime: srcUseClockTime,
			})
		);
	}

	/* Resolves one source profile per file extension. Asks the user when an extension has more than one profile.
	   Returns null if the user cancels. */
	async function resolveProfilesByExt() {
		let profileByExt = {};

		for (const ext of new Set(files.map(getFileExt))) {
			let possibleProfiles = formats.srcProfiles[ext] || [];

			if (ext === srcExt && srcProfile) {
				profileByExt[ext] = srcProfile;
			} else if (possibleProfiles.length === 1) {
				profileByExt[ext] = possibleProfiles[0];
			} else if (possibleProfiles.length > 1) {
				let profileRes = await Swal.fire({
					...SWAL_BASE,
					customClass: SWAL_CLASSES.ok,
					title: m.editor_select_profile_title(),
					text: m.editor_select_profile_text({fileType: ext.toUpperCase()}),
					input: "select",
					inputOptions: possibleProfiles.reduce((acc, profile) => {
						acc[profile] = formats.profileMapping[profile].name;
						return acc;
					}, {}),
					inputValue: possibleProfiles[0],
					allowOutsideClick: false,
					allowEscapeKey: false,
				});

				if (!profileRes.isConfirmed || !profileRes.value) {
					return null;
				}

				profileByExt[ext] = profileRes.value;
			}
		}

		return profileByExt;
	}

	/* Imports every selected file into its own new event group, one file at a time */
	async function importMultipleFiles() {
		let profileByExt = await resolveProfilesByExt();
		if (!profileByExt) {
			toast.push(m.editor_import_cancelled(), { classes: ["toast-warning"] });
			return;
		}

		let promptAnswers = {};
		let importedCount = 0;
		let failures = [];

		for (const file of files) {
			try {
				let ext = getFileExt(file);
				let profile = profileByExt[ext];
				if (!profile) {
					throw new Error(m.editor_unsupported_file_type_toast());
				}

				let useClockTime = profile === srcProfile ? srcUseClockTime : getUseClockTimeDefault(profile);
				let contents = await readSubtitleFileContents(file, {
					srcExt: ext,
					srcProfile: profile,
					fileEncoding: fileEncoding,
				});
				let decodedEvGroup = await decodeAndCleanUp(contents, profile, useClockTime, promptAnswers);

				let groupIndex = createSubtitleEventGroup(getFileBaseName(file));
				eventGroupState[groupIndex].events = decodedEvGroup.events;
				projectState.selected = groupIndex;
				importedCount++;
			} catch (err) {
				console.log(err, err.message);
				failures.push(m.subtitle_import_multi_file_failed_toast({ file: file.name, error: err.message }));
			}
		}

		if (importedCount > 0) {
			insertHistoryAction({
				name: "import subtitles", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			toast.push(m.subtitle_import_multi_done_toast({ count: importedCount }), {
				classes: ["toast-success"],
			});
		}

		failures.forEach((failure) => {
			toast.push(failure, { classes: ["toast-danger"] });
		});

		saveImportDefaults();

		if (importedCount > 0) {
			hideModal();
		}
	}

	async function importSubtitleFile() {
		toast.push(m.subtitle_import_importing_toast(), {
            classes: ["toast-warning"],
        });

		try {
			srcDropFrame = isSrcDropFrameEnabled();
			srcIncode = tcLib.tcValidate(
				srcIncode,
				parseFloat(srcFrameRate),
				srcDropFrame,
			);

			if (isMultiFile) {
				await importMultipleFiles();
				return;
			}

			let decodedEvGroup = await decodeAndCleanUp(fileContents, srcProfile, srcUseClockTime, {});

			/* Create or select the event group */
			if (eventGroup === "new") {
				projectState.selected = createSubtitleEventGroup(srcFileName);
			} else {
				projectState.selected = eventGroupState.findIndex((group) => {
					return group.id === eventGroup;
				});
			}

			if (eventGroup === "new" || importOption === "replace") {
				eventGroupState[projectState.selected].events = decodedEvGroup.events;
			} else {
				//Optionally assign imported events to a track when merging with an existing group
				if (TRACK_LETTERS.includes(importTrack)) {
					decodedEvGroup.events.forEach((event) => {
						event.track = importTrack;
					});
				}

				eventGroupState[projectState.selected].events = [...eventGroupState[projectState.selected].events, ...decodedEvGroup.events];
			}

			insertHistoryAction({
				name: "import subtitles", //action name
				eventGroup: projectState.selected,
				snapshots: [
					{
						store: "eventGroupState",
						value: JSON.stringify(eventGroupState),
					},
				],
			});

			saveImportDefaults();

			hideModal();
		} catch (err) {
			console.log(err, err.message);
			toast.push(
                m.subtitle_import_failed_toast({ error: err.message }),
                { classes: ["toast-danger"] },
            );
		}
	}
</script>

<div transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'subtitleImport' ? 'show d-block' : ''}" 
    role="dialog"
    aria-labelledby="subtitleImportTitle"
    aria-modal="true" 
    tabindex="-1" 
    id="SubtitleImportModal">
	<div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title" id="subtitleImportTitle">
                    {m.subtitle_import_title()}
                </h4>
				<button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal}></button>
			</div>
			<div class="modal-body">
				<p class="small text-muted"><i class="bi bi-lightning-fill"></i> {m.subtitle_import_powered_by()}</p>

				<div class="row">
					<div class="col">
						<!-- Import Form Start -->
						<form onsubmit={preventDefault(importSubtitleFile)} aria-label={m.subtitle_import_form_label()}>
							<!-- File Input -->
							<div class="mb-3 shadow bg-secondary rounded p-3">
								<label class="form-label" id="fileInputLabel" for="subtitleFilePicker">{m.subtitle_import_select_file_label()} <i class="bi bi-info-circle" aria-label={m.subtitle_import_supported_formats_label({ formats: allowedExtensions })}></i></label>
								<input type="file" class="form-control" id="subtitleFilePicker" onchange={handleFileInput} accept={allowedExtensions} aria-labelledby="fileInputLabel" multiple />
								{#if isMultiFile}
									<p class="small text-warning mt-2 mb-0"><i class="bi bi-files"></i> {m.subtitle_import_multi_files_note({ count: files.length })}</p>
								{/if}
							</div>
							<div class="row">
								<!-- Target Event Group Option Start -->
								<div class="mb-3 col-6">
									<label class="form-label text-warning" for="EventGroupSelection">{m.subtitle_import_event_group_label()}</label>
									<select class="form-select" bind:value={eventGroup} disabled={isMultiFile}>
										{#each eventGroupState as eventGroupOption (eventGroupOption.id)}
											<option value={eventGroupOption.id}>{eventGroupOption.name}</option>
										{/each}
										<option class="fw-bold" value="new">{m.new_badge()}</option>
									</select>
								</div>
								<!-- Target Event Group Option End -->
								<!-- Merge or Replace Option Start -->
								{#if !isMultiFile}
									<div class="mb-3 col-6">
										<p class="text-warning">{m.subtitle_import_merge_replace_label()} <i class="bi bi-info-circle" title={m.subtitle_import_merge_replace_help()}></i></p>

										<div class="form-check">
											<input class="form-check-input" disabled={eventGroup === 'new'} type="radio" bind:group={importOption} name="importOption" id="mergeImportOption" value="merge" checked />
											<label class="form-check-label" for="mergeImportOption"> {m.subtitle_import_merge_label()} </label>
										</div>
										<div class="form-check">
											<input class="form-check-input" disabled={eventGroup === 'new'} type="radio" bind:group={importOption} name="importOption" id="replaceImportOption" value="replace" />
											<label class="form-check-label" for="replaceImportOption"> {m.subtitle_import_replace_label()} </label>
										</div>
										<!-- Optional Track Assignment (merge only) -->
										<div class="mt-2">
											<label class="form-label" for="importTrackSelect">{m.event_track_import_label()} <i class="bi bi-info-circle" title={m.event_track_import_help()}></i></label>
											<select id="importTrackSelect" class="form-select form-select-sm" bind:value={importTrack} disabled={eventGroup === 'new' || importOption !== 'merge'}>
												<option value="">{m.event_track_none()}</option>
												{#each TRACK_LETTERS as letter (letter)}
													<option value={letter}>{m.multiview_track()} {letter}</option>
												{/each}
											</select>
										</div>
									</div>
								{/if}
                                    <!-- Language -->
									<div class="mb-3 col-12">
										<label class="form-label text-warning" for="Language">{m.subtitle_import_group_language_label()}</label>
										<LanguageSelect
											id="Language"
											class="form-control"
											bind:value={language}
											disabled={eventGroup !== 'new'} />
									</div>
									<div class="mb-3 col-12">
										<div class="form-check form-switch mb-2">
											<input type="checkbox" class="form-check-input" id="rtlSwitch" bind:checked={rtl} disabled={eventGroup !== 'new'}/>
											<label class="form-check-label" for="rtlSwitch">{m.right_to_left()}</label>
										</div>
									</div>
								<!-- Merge or Replace Option End -->
							</div>
							<!-- Source Profile List -->
							{#if formats.srcProfiles[srcExt] && formats.srcProfiles[srcExt].length > 1}
								<div class="mb-3">
									<label class="form-label" for="srcProfileSelectList">{m.subtitle_import_source_profile_label()} <i class="bi bi-asterisk text-danger"></i></label>
									<select id="srcProfileSelectList" class="form-select" bind:value={srcProfile} onchange={() => {
										updateSrcUseClockTime();
										updateFileContents();
									}}>
										{#each formats.srcProfiles[srcExt] as profile (profile)}
											<option>{profile}</option>
										{/each}
									</select>
								</div>
							{/if}
							<button
								type="button"
								class="btn btn-outline-warning mb-2 dropdown-toggle"
								onclick={() => {
									showOptions = !showOptions;
								}}>{m.more_options()}</button>
							{#if showOptions}
								<div id="advancedOptions" class="p-3 bg-secondary shadow rounded">
									<div class="row pt-2 mb-2">
										<h5 class="col-12 lead text-warning">{m.subtitle_import_general_options()}</h5>
									</div>
									
                                    <div class="row mb-2">
                                        <div class="mb-3 col-8">
											<label class="form-label" for="srcIncode">{m.subtitle_export_incode_label()}</label>
                                            <input
												type="text"
												class="form-control"
												id="srcIncode"
												bind:value={srcIncode}
												onblur={validateSrcIncode}
												onfocus={(e) => {
													e.target.select();
												}} />
                                        </div>
                                        <div class="mb-3 col-4">
											<label class="form-label" for="srcUseClockTime">{m.subtitle_export_use_clock_time()}</label>
                                            <select id="srcUseClockTime" class="form-select" bind:value={srcUseClockTime}>
												<option value="yes">{m.subtitle_export_yes()}</option>
												<option value="no">{m.subtitle_export_no()}</option>
                                            </select>
                                        </div>
                                        <div class="col-8">
											<label class="form-label" for="SrcFrameRateSelect">{m.subtitle_import_source_frame_rate_label()}</label>
                                            <select id="SrcFrameRateSelect" class="form-select" bind:value={srcFrameRate}>
                                                {#each frameRates.frameRates as frameRate (frameRate)}
                                                    <option>{frameRate}</option>
                                                {/each}
                                            </select>
                                        </div>
                                        {#if parseFloat(srcFrameRate) === 29.97 || parseFloat(srcFrameRate) === 59.94}
                                            <div class="col-4">
												<label class="form-label" for="SrcDropFrameSelect">{m.subtitle_import_drop_frame_label()}</label>
                                                <select id="SrcDropFrameSelect" class="form-select" bind:value={srcDropFrame}>
													<option value="true">{m.common_true()}</option>
													<option value="false">{m.common_false()}</option>
                                                </select>
                                            </div>
                                        {/if}
                                    </div>
									<div class="mb-3 mt-2">
										<label class="form-label" for="srcFileEncoding">{m.subtitle_import_file_encoding_label()}</label>
										<select id="srcFileEncoding" class="form-select" bind:value={fileEncoding} onchange={updateFileContents}>
											{#each encodings as encodingOption (encodingOption)}
												<option>{encodingOption}</option>
											{/each}
										</select>
									</div>
									<div class="form-check">
										<input class="form-check-input" type="checkbox" id="dropFrameConvert" bind:checked={dropFrameConvert} />
										<label class="form-check-label" for="dropFrameConvert">
											{m.subtitle_import_drop_frame_convert_label()}
											<i class="bi bi-info-circle" title={m.subtitle_import_drop_frame_convert_help()}></i>
										</label>
									</div>
									{#if srcProfile && formats.profileMapping[srcProfile].options && formats.profileMapping[srcProfile].options.decode.length > 0}
										<div class="row pt-4 mt-4 mb-3 border-top border-secondary">
											<h5 class="col-12 lead text-warning">{m.subtitle_import_decoding_options_label()}</h5>
											{#each formats.profileMapping[srcProfile].options.decode as decodeOption (decodeOption.name)}
												<div class="mb-3 col-6">
													<label class="form-label" for="decodingOption">{decodeOption.name}</label>
													{#if decodeOption.type === "textarea"}
														<textarea class="form-control" rows="3" bind:value={decodeOption.selected}></textarea>
													{:else if decodeOption.type === "text-input"}
														<input class="form-control" type="text" bind:value={decodeOption.selected} />
													{:else if decodeOption.type === "number-input"}
														<input class="form-control" type="number" bind:value={decodeOption.selected} />
													{:else}
														<select class="form-select" bind:value={decodeOption.selected}>
															{#each decodeOption.values as valueOption (valueOption)}
																<option>{valueOption}</option>
															{/each}
														</select>
													{/if}
												</div>
											{/each}
										</div>
									{/if}
								</div>
								<!-- End of Advanced Settings -->
							{/if}
						</form>
						<!-- Import Form End -->
					</div>
					<div class="col">
						<!-- File Preview -->
						<form id="SourcePreviewForm">
							<div class="mb-3">
								<label class="form-label" for="srcPreviewText">{m.subtitle_import_source_preview_label()}</label>
								<textarea bind:value={fileContents} disabled class="form-control" id="srcPreviewText" rows="10" placeholder={m.subtitle_import_source_preview_placeholder()}></textarea>
							</div>
						</form>
					</div>
				</div>
			</div>
			<div class="modal-footer">
				<button class="btn btn-primary" type="button" onclick={importSubtitleFile} disabled={fileContents && fileContents.length === 0} aria-label={m.subtitle_import_action_label()}>{m.subtitle_import_action_label()} </button>
			</div>
		</div>
	</div>
</div>

<style lang="css">
	#advancedOptions {
		overflow-y: auto;
		overflow-x: none;
		max-height: 50vh;
	}
</style>
